import React from "react";
import { useTranslation } from "react-i18next";
import { theme } from "src/config";
import { User } from "src/models";
import styled from "styled-components";
import { Avatar, Icon } from ".";
import { IMultiSelectProps, MultiSelect, Option, ItemRendererProps,  } from "./multiSelect";
import { Text  } from "./text";

/**
 * Generic multi select component, styled to follow the OP theme
 *
 * @param options       Option[]  Options to choose from
 * @param selected      Option[]  Pre-selected values
 * @param singleSelect  ?boolean  User can only select one item
 * @param forceSelect   ?boolean  Force at least one selection
 * @param disabled      ?boolean  Disable input
 * @param placeholder   ?string   String shown in empty input field
 * @param onChange      ?(selection: Option | null): void Called when single Select is active
 * @param onAdd         ?(selection: Option): void        Called in a multiselect when an item was added
 * @param onRemove      ?(selection: Option): void        Called in a multiselect when an item was removed
 */
export const UserSelect = (props: IMultiSelectProps): JSX.Element => {
  const containerRef = React.useRef<HTMLDivElement>();
  const headerRef = React.useRef<HTMLDivElement>();
  const [width, setWidth] = React.useState<number>(0); // TODO use this to dertermine to add a <...> placeholder if there are too many mini avatars for the available space
  const { t, i18n } = useTranslation();
  const { 
    disabled, 
    selected, 
    singleSelect, 
    onLoad,
    showLoadMore,
  } = props;

  React.useEffect(() => {
    const headerEls: HTMLCollectionOf<HTMLDivElement> =
      containerRef?.current?.getElementsByClassName(
        "dropdown-heading-value"
      ) as HTMLCollectionOf<HTMLDivElement>;
    headerRef.current = headerEls.item(0);
  }, [containerRef]);
  React.useEffect(() => {
    setWidth(headerRef?.current?.clientWidth);
  }, [headerRef?.current?.clientWidth]);

  const loadMoreOptionKey = "#load_more";
  const loadMoreOption: Option = {
    key:   loadMoreOptionKey,
    label: "",
    value: (
      <LoadingButtonContainer>
        <Icon iconId="chevronDown" size="small" color={theme.color.grayLight} />
      </LoadingButtonContainer>
    ),
  }


  const itemRenderer = ({
    checked,
    option,
    disabled,
    onClick
  }: ItemRendererProps): React.ReactNode => {
    return (
      <div 
        className = {`item-renderer ${disabled ? "disabled" : ""}`}
      >
        <input
          type     = "checkbox"
          onChange = {option.key === loadMoreOptionKey ? onLoad : onClick}
          checked  = {checked}
          tabIndex = {-1}
          disabled = {disabled}
          style    = {{ height: 0, width: 0, visibility: 'hidden', position: 'absolute' }}
        />
        <span>
          {option.key === loadMoreOptionKey ? option.value : option.label}
        </span>
      </div>
    );
  };

  const valueRenderer = (items: Option[]) => {
    if (singleSelect && items.length) {
      const user: User = items[0].value;
      return (
        <SingleValueContainer>
          <Avatar size="input" border image={user.getAvatar} initials={user.getInitials} />
          <Text style={{marginLeft: "0.5rem"}} as="span">
            {user.displayName}
          </Text> 
        </SingleValueContainer>
      );
    } else if (items.length) {
      return (
        <MultiValueContainer>
          {items.map((i) => (
            <Avatar 
              size     = "input"
              key      = {i.label}
              initials = {(i.value as User).getInitials}
              image    = {(i.value as User).getAvatar}
              border
            />
          ))}
        </MultiValueContainer>
      );
    } else
      return (
        <SingleValueContainer>
          <Text color={"disabled"} as="span">{props.placeholder}</Text>
        </SingleValueContainer>
      );
  };

  return (
    <Container ref={containerRef} disabled={disabled}>
      {disabled ? (
        <DisabledContainer>
        <span>{props.label}</span>
        {(props.valueRenderer || valueRenderer)(selected)}
        </DisabledContainer>
      ) : (
        <UserMultiSelect
          {...props}
          closeOnSelect
          itemRenderer  = {props.itemRenderer || itemRenderer}
          valueRenderer = {props.valueRenderer || valueRenderer}
        />
      )}
    </Container>
  );
};

const UserMultiSelect = styled(MultiSelect)``;

const DisabledContainer = styled.div`
  display:     flex;
  flex-direction: column;
  span {
    font-size:   ${theme.font.size.xxsmall};
  }
`;

const SingleValueContainer = styled.div`
  font-size:   ${theme.font.size.small};
  display:     flex;
  align-items: center;
  div:first-child {
    /* margin-right: 0.25rem; */
  }
`;

const MultiValueContainer = styled.div`
  display:     flex;
  align-items: center;
  > div {
    margin-right: -0.4rem;
  }
`;

const Container = styled.div<{disabled?: boolean}>`
  display:       ${props => props.disabled ? "flex" : undefined};
  border-bottom: ${props => props.disabled ? `1px solid ${theme.color.disabled}`: undefined};
`;

const LoadingButtonContainer = styled.div`
  display:         flex;
  justify-content: center;
  align-items:     center;
`;
